'use client';

import * as React from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'motion/react';

import { BACKERS } from '~/lib/landing-content';

const LOGOS: Array<{ name: string; src: string; darkSrc?: string }> = [
  { name: 'AfterQuery', src: '/assets/logos/afterquery.png', darkSrc: '/assets/logos/afterquery-dark.png' },
  { name: 'Crative', src: '/assets/logos/crative.com.jpeg' },
  { name: 'Curanostics', src: '/assets/logos/curanostics.health.jpeg' },
  { name: 'Metal', src: '/assets/logos/metal.sog.jpeg' },
  { name: 'Solitude Labs', src: '/assets/logos/solitudelabs.com.jpeg' },
  { name: 'Ventus AI', src: '/assets/logos/ventusai.com.jpeg' },
  { name: 'Work and Solutions', src: '/assets/logos/workandsolutions.com.jpeg' },
  { name: 'Lifted', src: '/assets/logos/lifted.png' },
  {
    name: 'Remote Control Enterprises',
    src: '/assets/logos/remote-control.png',
    darkSrc: '/assets/logos/remote-control-dark.png'
  },
];

/**
 * Logo Marquee - Infinite scrolling logo wall below the hero.
 *
 * CSS-driven translateX animation (GPU-accelerated).
 * Logos duplicated to prevent visual gaps.
 * Grayscale in light mode, full-color in dark mode. Respects prefers-reduced-motion.
 */
export function LogoMarquee(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.section
      className="relative py-0"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      {/* Constrained "logo band": matches the content max-width and padding
          used by the hero + every other section, so the scrolling logos stay
          inside the page-edge vertical hairlines on .marketing-shell.
          The local border-y is intentional, giving the logos a tight framed strip
          rather than the looser look you get from leaving section boundaries
          to <SectionDivider />. Those dividers are omitted on either side of
          the marquee in page.tsx to avoid double-rules. */}
      <div className="logo-marquee-band mx-auto w-full max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16 py-3 md:py-4 border-y border-[color:var(--hairline-2)] flex items-center">
        {/* Backers hold the left of the band; customer logos scroll to the
            right. Sisu is listed as a backer only, not as a customer (Abu, Nov 2). */}
        {BACKERS.enabled && (
          <div className="flex shrink-0 pr-4 md:pl-6 md:pr-10">
            {/* Mint pill keeps the backers visibly apart from the customer
                logos, and the inset keeps it off the band's borders. */}
            <div
              className="inline-flex flex-col items-center gap-1 rounded-2xl border px-3 py-1.5 md:h-[50px] md:flex-row md:gap-3 md:rounded-full md:px-5 md:py-0"
              style={{
                borderColor: 'color-mix(in srgb, #00C896 35%, transparent)',
                background: 'color-mix(in srgb, #00C896 6%, var(--paper))',
                color: 'var(--accent-darker)'
              }}
            >
              <span className="font-mono text-[9px] font-medium uppercase tracking-[0.08em] md:text-[10.5px]">
                {BACKERS.label}
              </span>
              <span
                aria-hidden
                className="h-px w-full md:h-4 md:w-px"
                style={{ background: 'color-mix(in srgb, #00C896 35%, transparent)' }}
              />
              <div className="flex items-center gap-3 md:gap-4">
                {BACKERS.list.map((b) => (
                  <a
                    key={b.id}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={b.name}
                    className="inline-flex rounded transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--paper)]"
                  >
                    <Image
                      data-logo-adaptive
                      src={`/assets/backers/${b.id}.png`}
                      alt={b.name}
                      width={110}
                      height={26}
                      loading="eager"
                      className="h-5 w-auto object-contain md:h-8"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Marquee viewport: only this region is masked + clipped. */}
        <div className="relative min-w-0 flex-1 overflow-hidden">
          {/* Gradient edge masks */}
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 md:w-20 z-10 bg-gradient-to-r from-[color:var(--paper)] to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 md:w-20 z-10 bg-gradient-to-l from-[color:var(--paper)] to-transparent" />

          {/* Scrolling track: 3 duplicated copies so one full set is always
              off-screen waiting to enter. Animation translates by exactly one
              copy-width (-33.3334%) so the loop point lands on identical
              content. The animation is applied via a scoped class (not an
              inline style) so styled-jsx's keyframe-name scoping can never
              break the reference. */}
          <div className={`flex w-max ${shouldReduceMotion ? '' : 'marquee-track'}`}>
            {Array.from({ length: 3 }).flatMap((_, copy) =>
              LOGOS.map((logo, i) => (
                <div
                  key={`${logo.name}-${copy}-${i}`}
                  className="flex items-center justify-center mx-5 md:mx-14 shrink-0"
                  aria-hidden={copy > 0}
                >
                  {/* loading="eager": the track scrolls continuously, so a
                      logo lazy-loading as it enters from the right edge would
                      flash a blank gap on its first pass (the "gap on the
                      right", Abu Jun 12). Only ~10 unique small logos, so
                      eager-loading all of them is cheap and the strip is
                      always full. */}
                  {logo.darkSrc ? (
                    <>
                      <Image
                        src={logo.src}
                        alt={logo.name}
                        width={160}
                        height={56}
                        loading="eager"
                        className="logo-marquee-image h-9 md:h-14 w-auto object-contain grayscale opacity-80 transition-all duration-300 hover:grayscale-0 hover:opacity-100 dark:hidden"
                      />
                      <Image
                        src={logo.darkSrc}
                        alt={logo.name}
                        width={160}
                        height={56}
                        loading="eager"
                        className="logo-marquee-image hidden h-9 w-auto object-contain opacity-100 transition-all duration-300 dark:block md:h-14"
                      />
                    </>
                  ) : (
                    <Image
                      data-logo-adaptive
                      src={logo.src}
                      alt={logo.name}
                      width={160}
                      height={56}
                      loading="eager"
                      className="logo-marquee-image h-9 md:h-14 w-auto object-contain grayscale opacity-80 transition-all duration-300 hover:grayscale-0 hover:opacity-100"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* styled-jsx is a Next.js-supported style attribute. */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>{`
        @keyframes marquee {
          from {
            transform: translateX(0);
          }
          to {
            /* Exactly one of the three copies. calc keeps the seam
               drift-free (the old -33.3334% over-translated ~0.3px each
               loop, a slow sub-pixel hitch); -100%/3 lands the loop point
               on identical content every time — a true circle. */
            transform: translateX(calc(-100% / 3));
          }
        }

        .marquee-track {
          animation: marquee 36s linear infinite;
        }

        :global(.dark .logo-marquee-image) {
          filter: none;
          opacity: 1;
        }
      `}</style>
    </motion.section>
  );
}
